/**
 * Path Users Modal
 *
 * Shows the individual anonymous IDs behind a conversion path so they
 * can be looked up in the User Journey report. Each ID is clickable
 * (opens User Journey in a new tab) and copyable.
 */

import { useState, useEffect, useMemo } from 'react'
import { trafficAPI } from '../services/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog'
import { Button } from './ui/button'
import { Loader2, AlertCircle, Copy, Check, ExternalLink } from 'lucide-react'
import { PathVisualization } from './PathsTable'

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'converted', label: 'Converted' },
  { value: 'notConverted', label: 'Not Converted' },
]

export default function PathUsersModal({ isOpen, onClose, pathItem, queryParams }) {
  const [users, setUsers] = useState([])
  const [totalUsers, setTotalUsers] = useState(0)
  const [limit, setLimit] = useState(500)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [copiedAll, setCopiedAll] = useState(false)
  const [copiedId, setCopiedId] = useState(null)

  // Fetch users when the modal opens for a path
  useEffect(() => {
    if (!isOpen || !pathItem?.path) return

    let cancelled = false
    const fetchUsers = async () => {
      setLoading(true)
      setError(null)
      setUsers([])
      setFilter('all')
      try {
        const response = await trafficAPI.getAttributionPathUsers({
          ...queryParams,
          path: pathItem.path,
        })
        if (cancelled) return
        if (response.data?.data) {
          setUsers(response.data.data.users || [])
          setTotalUsers(response.data.data.totalUsers || 0)
          setLimit(response.data.data.limit || 500)
        }
      } catch (err) {
        if (cancelled) return
        console.error('[PATH USERS] Error:', err)
        setError(err.response?.data?.error?.message || 'Failed to load users for this path')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchUsers()
    return () => { cancelled = true }
  }, [isOpen, pathItem?.path]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredUsers = useMemo(() => {
    if (filter === 'converted') return users.filter(u => u.converted)
    if (filter === 'notConverted') return users.filter(u => !u.converted)
    return users
  }, [users, filter])

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(filteredUsers.map(u => u.anonymousId).join('\n'))
      setCopiedAll(true)
      setTimeout(() => setCopiedAll(false), 2000)
    } catch (err) {
      console.error('[PATH USERS] Clipboard error:', err)
    }
  }

  const handleCopyId = async (anonymousId) => {
    try {
      await navigator.clipboard.writeText(anonymousId)
      setCopiedId(anonymousId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('[PATH USERS] Clipboard error:', err)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Users on this Path</DialogTitle>
          <DialogDescription>
            Anonymous IDs for this journey path — click one to open it in the User Journey report
          </DialogDescription>
        </DialogHeader>

        {/* Path visualization */}
        {pathItem && (
          <div className="py-1">
            <PathVisualization pathArray={pathItem.pathArray || pathItem.path?.split(' -> ')} />
          </div>
        )}

        {/* Toolbar: filter + copy all */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  filter === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
                {!loading && (
                  <span className="ml-1 opacity-70">
                    {opt.value === 'all' ? users.length
                      : opt.value === 'converted' ? users.filter(u => u.converted).length
                      : users.filter(u => !u.converted).length}
                  </span>
                )}
              </button>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopyAll}
            disabled={loading || filteredUsers.length === 0}
          >
            {copiedAll ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
            {copiedAll ? 'Copied!' : `Copy ${filteredUsers.length} IDs`}
          </Button>
        </div>

        {/* User list */}
        <div className="border border-border rounded-lg max-h-[360px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading users...
            </div>
          ) : error ? (
            <div className="flex items-center justify-center p-8 text-destructive">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No users found for this filter
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <tbody className="divide-y divide-border">
                {filteredUsers.map((user) => (
                  <tr key={user.anonymousId} className="hover:bg-muted/50">
                    <td className="px-4 py-2">
                      <a
                        href={`/reports/user-journey?id=${encodeURIComponent(user.anonymousId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:underline"
                        title="Open in User Journey report"
                      >
                        {user.anonymousId}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded"
                        style={user.converted
                          ? { backgroundColor: '#22C55E', color: '#FFFFFF' }
                          : { backgroundColor: '#6B7280', color: '#FFFFFF' }}
                      >
                        {user.converted ? 'Converted' : 'Not Converted'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleCopyId(user.anonymousId)}
                        className="p-1 text-muted-foreground hover:text-foreground"
                        title="Copy ID"
                      >
                        {copiedId === user.anonymousId
                          ? <Check className="w-3.5 h-3.5" />
                          : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Truncation note */}
        {!loading && !error && totalUsers > users.length && (
          <p className="text-xs text-muted-foreground">
            Showing first {users.length.toLocaleString()} of {totalUsers.toLocaleString()} users (capped at {limit.toLocaleString()})
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
